import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  X, Search, Loader2, ChevronDown, ChevronRight, CheckSquare, Square, Download, Upload, Info,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { posApi } from '@/services/api';
import { formatMoney } from '@/utils/currency';
import { useBulkItemSelection } from '@/hooks/useBulkItemSelection';
import { downloadBulkProductUpdateExcel } from '@/utils/bulkProductUpdateExcel';
import EntityImportWizard from '@/components/dataImport/EntityImportWizard';
import { getProductUpdateImportConfig } from '@/components/dataImport/entities/productImportConfigs';
import type { POSItem } from '@/types';

interface Props {
  onClose: () => void;
}

export default function BulkProductUpdateModal({ onClose }: Props) {
  const { t } = useTranslation();
  const sel = useBulkItemSelection('');
  const [selectingAll, setSelectingAll] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const { data: customFields = [] } = useQuery({
    queryKey: ['customFields'],
    queryFn: async () => {
      const res = await posApi.getCustomFields();
      return res.data.message || [];
    },
  });

  const importConfig = getProductUpdateImportConfig(customFields);

  const handleSelectAll = async () => {
    setSelectingAll(true);
    try {
      await sel.selectAllVisible();
    } finally {
      setSelectingAll(false);
    }
  };

  const handleDownload = () => {
    if (sel.selectedRows.length === 0) {
      toast.error(t('items.noProductsSelected'));
      return;
    }
    downloadBulkProductUpdateExcel(sel.selectedRows, customFields);
    toast.success(t('items.bulkUpdateDownloaded', { count: sel.selectedRows.length }));
  };

  const renderSelectRow = (row: ReturnType<typeof useBulkItemSelection>['selectedRows'][0]) => {
    const checked = sel.selectedKeys.has(row.key);
    return (
      <button
        key={row.key}
        type="button"
        onClick={() => sel.toggleKey(row.key)}
        className={`w-full flex items-center gap-2 px-3 py-2.5 border-b border-gray-50 text-left transition-colors hover:bg-gray-50 ${checked ? 'bg-primary-50/60' : ''}`}
      >
        <span className="shrink-0 text-primary-600">
          {checked ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4 text-gray-400" />}
        </span>
        <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate">{row.label}</span>
        <span className="text-sm text-gray-600 shrink-0">{formatMoney(row.salePrice)}</span>
      </button>
    );
  };

  const renderItemRow = (item: POSItem) => {
    const expanded = !!sel.expandedItems[item.name];
    const variants = sel.loadedVariants[item.name];
    const loading = !!sel.loadingVariants[item.name];

    if (item.has_variants) {
      return (
        <div key={item.name} className="border-b border-gray-100">
          <button
            type="button"
            onClick={() => void sel.toggleExpand(item)}
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left"
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <span className="text-sm font-medium text-gray-900 flex-1 truncate">{item.item_name}</span>
            {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
          </button>
          {expanded && variants?.map((v) => {
            const key = `${item.name}__${v.name}`;
            const row = sel.flatRows.find((r) => r.key === key);
            if (!row) return null;
            return renderSelectRow(row);
          })}
        </div>
      );
    }

    const row = sel.flatRows.find((r) => r.key === item.name);
    if (!row) return null;
    return renderSelectRow(row);
  };

  if (showImport) {
    return (
      <EntityImportWizard
        config={importConfig}
        onClose={() => setShowImport(false)}
        onSuccess={onClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{t('items.bulkUpdate')}</h2>
            <p className="text-sm text-gray-500">{t('items.bulkUpdateHelp')}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="w-80 border-r border-gray-100 flex flex-col shrink-0">
            <div className="p-3 space-y-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  className="input-field pl-9 text-sm"
                  placeholder={t('common.search')}
                  value={sel.search}
                  onChange={(e) => sel.setSearch(e.target.value)}
                />
              </div>
              <select className="input-field text-sm" value={sel.category} onChange={(e) => sel.setCategory(e.target.value)}>
                <option value="All">{t('items.allCategories')}</option>
                {sel.categories.map((c) => (
                  <option key={c.name} value={c.name}>{c.category_name}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => void handleSelectAll()}
                  disabled={selectingAll}
                  className="btn-secondary text-xs flex-1 py-2 flex items-center justify-center gap-1"
                >
                  {selectingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                  {t('items.selectAll')}
                </button>
                <button type="button" onClick={sel.clearSelection} className="btn-secondary text-xs flex-1 py-2">
                  {t('items.clearSelection')}
                </button>
              </div>
              <p className="text-xs text-gray-500">{t('items.selectedCount', { count: sel.selectedKeys.size })}</p>
            </div>
            <div className="flex-1 overflow-auto">
              {sel.isLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
              ) : (
                sel.searchResults.map((item) => renderItemRow(item))
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col min-w-0 p-6">
            <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4 mb-6">
              <div className="flex gap-2 items-start">
                <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900 space-y-2">
                  <p className="font-semibold">{t('items.bulkUpdateInstructionsTitle')}</p>
                  <ol className="list-decimal list-inside space-y-1 text-blue-800">
                    <li>{t('items.bulkUpdateStep1')}</li>
                    <li>{t('items.bulkUpdateStep2')}</li>
                    <li>{t('items.bulkUpdateStep3')}</li>
                    <li>{t('items.bulkUpdateStep4')}</li>
                  </ol>
                  <p className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-2">
                    {t('items.bulkUpdateIdWarning')}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-auto">
              <button
                type="button"
                onClick={handleDownload}
                disabled={sel.selectedRows.length === 0}
                className="btn-primary flex flex-col items-center justify-center gap-2 py-8 px-4 h-full min-h-[140px]"
              >
                <Download className="w-8 h-8" />
                <span className="font-semibold">{t('items.downloadBulkUpdate')}</span>
                <span className="text-xs opacity-80 text-center">
                  {t('items.exportCount', { count: sel.selectedRows.length })}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setShowImport(true)}
                className="btn-secondary flex flex-col items-center justify-center gap-2 py-8 px-4 h-full min-h-[140px]"
              >
                <Upload className="w-8 h-8" />
                <span className="font-semibold">{t('items.importBulkUpdate')}</span>
                <span className="text-xs text-gray-500 text-center">{t('items.importBulkUpdateHelp')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
